import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { useSite } from "../../context/SiteContext";
import { api, fileUrl, formatErr } from "../../lib/api";
import { Empty, Field, PageHeader, UploadButton, inputCls } from "../kit";

function Input_({ f, v, onChange }) {
  const [k, , type] = f;
  if (type === "textarea") return <Textarea rows={4} value={v || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`col-field-${k}`} />;
  if (type === "bool") return <Switch checked={!!v} onCheckedChange={onChange} data-testid={`col-field-${k}`} />;
  if (type === "number") return <Input type="number" value={v ?? ""} onChange={(e) => onChange(Number(e.target.value))} className={inputCls} data-testid={`col-field-${k}`} />;
  if (type === "list") return <Textarea rows={4} value={(v || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} className={inputCls} data-testid={`col-field-${k}`} />;
  if (type === "image" || type === "video") {
    const accept = type === "video" ? "video/*" : "image/*";
    return (
      <div className="space-y-2">
        {v && type === "image" && <img src={fileUrl(v)} alt="" className="max-h-40 rounded-lg border border-[#007bff]/30" />}
        {v && type === "video" && !/youtu/.test(v) && <video src={fileUrl(v)} controls className="max-h-48 rounded-lg" />}
        <div className="flex gap-2"><Input value={v || ""} onChange={(e) => onChange(e.target.value)} placeholder={type === "video" ? "Upload a file or paste a YouTube link" : "Image URL"} className={inputCls} />
          <UploadButton scope="public" accept={accept} onUploaded={(r) => onChange(r.url)} testid={`col-upload-${k}`}>{v ? "Replace" : "Upload"}</UploadButton></div>
      </div>
    );
  }
  return <Input value={v || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`col-field-${k}`} />;
}

export function Collection({ name, title, desc, fields, onChange, embedded, defaults = {} }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/admin/${name}`).then((r) => setItems(r.data)), [name]);
  useEffect(() => {
    load();
  }, [load]);
  const done = () => {
    load();
    onChange?.();
  };
  const save = async () => {
    const body = { ...edit };
    if (body.bullets) body.bullets = body.bullets.map((b) => b.trim()).filter(Boolean);
    try {
      if (edit.id) await api.put(`/admin/${name}/${edit.id}`, body);
      else await api.post(`/admin/${name}`, body);
      toast.success("Saved");
      setEdit(null);
      done();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    await api.delete(`/admin/${name}/${id}`);
    done();
  };
  const toggle = async (it, key) => {
    await api.put(`/admin/${name}/${it.id}`, { [key]: !it[key] });
    done();
  };
  const head = fields[0][0];
  const boolKeys = fields.filter((f) => f[2] === "bool").map((f) => f[0]);
  const img = fields.find((f) => f[2] === "image")?.[0];
  return (
    <div data-testid={`collection-${name}`}>
      {embedded ? <div className="flex justify-end mb-3"><button onClick={() => setEdit({ ...defaults })} className="btn-electric !py-2" data-testid={`${name}-new-btn`}><Plus className="w-4 h-4" /> Add</button></div> : (
        <PageHeader title={title} desc={desc}><button onClick={() => setEdit({ ...defaults })} className="btn-electric !py-2" data-testid={`${name}-new-btn`}><Plus className="w-4 h-4" /> Add new</button></PageHeader>
      )}
      {items.length === 0 ? <Empty text="Nothing here yet." /> : (
        <div className="grid gap-3">
          {items.map((it) => (
            <div key={it.id} className="card-neon p-4 flex flex-wrap items-center gap-4" data-testid={`${name}-item-${it.id}`}>
              {img && it[img] && <img src={fileUrl(it[img])} alt="" className="w-20 h-14 object-cover rounded" />}
              <div className="flex-1 min-w-[180px]"><p className="font-semibold text-white">{it[head] || "(untitled)"}</p><p className="text-sm text-slate-400 line-clamp-1">{it[fields[1][0]]}</p></div>
              {boolKeys.map((b) => <label key={b} className="flex items-center gap-2 text-xs text-slate-400 capitalize"><Switch checked={!!it[b]} onCheckedChange={() => toggle(it, b)} data-testid={`${name}-toggle-${b}-${it.id}`} />{b}</label>)}
              <button onClick={() => setEdit(it)} className="btn-ghost !py-1.5 !px-3 text-sm" data-testid={`${name}-edit-${it.id}`}>Edit</button>
              <button onClick={() => del(it.id)} className="btn-ghost !py-1.5 !px-3 text-sm !text-rose-300 !border-rose-500/40" data-testid={`${name}-delete-${it.id}`}>Delete</button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#050a18] border-[#007bff]/40">
          <DialogTitle className="font-cond uppercase">{edit?.id ? "Edit" : "Add"} {title}</DialogTitle>
          {edit && (
            <div className="grid gap-3">
              {fields.map((f) => <Field key={f[0]} label={f[1]}><Input_ f={f} v={edit[f[0]]} onChange={(v) => setEdit({ ...edit, [f[0]]: v })} /></Field>)}
              <div className="flex gap-2"><button onClick={save} className="btn-electric !py-2" data-testid={`${name}-save-btn`}>Save</button><button onClick={() => setEdit(null)} className="btn-ghost !py-2">Cancel</button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ReviewsAdmin() {
  const { reload } = useSite();
  return <Collection name="reviews" title="Reviews" desc="Only published reviews appear on the website. Featured reviews show first." onChange={reload} defaults={{ published: true, featured: false, rating: 5 }}
    fields={[["name", "Customer / display name"], ["text", "Review text", "textarea"], ["image", "Review screenshot", "image"], ["service", "Service (optional)"], ["rating", "Star rating (0-5)", "number"], ["featured", "Featured", "bool"], ["published", "Published", "bool"]]} />;
}

export function VideosAdmin() {
  const { reload } = useSite();
  return <Collection name="videos" title="Video Tutorials" desc="Published videos appear in the homepage Video Tutorial section." onChange={reload} defaults={{ published: true }}
    fields={[["title", "Title"], ["description", "Description", "textarea"], ["video_url", "Video file", "video"], ["thumbnail", "Thumbnail", "image"], ["published", "Published", "bool"]]} />;
}

export function FaqsAdmin() {
  const { reload } = useSite();
  return <Collection name="faqs" title="FAQs" desc="Shown on the website and used by the AI chatbot to answer questions." onChange={reload} defaults={{ published: true, order: 10 }}
    fields={[["question", "Question"], ["answer", "Answer", "textarea"], ["order", "Display order", "number"], ["published", "Published", "bool"]]} />;
}
